// src/pages/ChatHistoryPage.tsx
// Displays all chat sessions in a full page view

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import ThemeToggle from '../components/ThemeToggle';
import { getRandomTagline } from '../utils/taglines';

interface ChatSession {
  session_id: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  preview: string;
}

const ChatHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async (loadMore = false) => {
    try {
      const currentOffset = loadMore ? offset : 0;
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_BASE_URL}/chat/sessions?offset=${currentOffset}&limit=30`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load sessions');
      }

      const data = await response.json();

      if (loadMore) {
        // Append new sessions
        setSessions(prev => [...prev, ...(data.sessions || [])]);
        setOffset(currentOffset + 30);
      } else {
        // Replace sessions (first load)
        setSessions(data.sessions || []);
        setOffset(30);
      }

      setHasMore(data.has_more || false);
    } catch (error) {
      console.error('[CHAT HISTORY] Error loading sessions:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    loadSessions(true);
  };

  const handleSessionClick = (sessionId: string) => {
    navigate(`/codemate/${sessionId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <img
              src="/cubemate-logo.png"
              alt="Cubemate"
              className="w-10 h-10 rounded-lg"
            />
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-brand bg-clip-text text-transparent">
                  Cubemate
                </h1>
              </div>
              <p className="text-xs text-muted-foreground/80 font-normal italic">
                {getRandomTagline()}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Body: Sidebar + Main Content */}
      <div className="flex flex-1 overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-primary/10 via-transparent to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-brand/10 via-transparent to-transparent rounded-full blur-3xl animate-pulse-slower"></div>
        </div>

        {/* Sidebar */}
        <Sidebar />

        {/* Chat Sessions List */}
        <div className="flex-1 overflow-y-auto relative z-10">
          <div className="max-w-4xl mx-auto py-8 px-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <svg className="w-8 h-8 animate-pulse" viewBox="0 0 24 24" fill="none">
                    <defs>
                      <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{ stopColor: 'rgb(147, 51, 234)', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: 'rgb(59, 130, 246)', stopOpacity: 1 }} />
                      </linearGradient>
                    </defs>
                    <path
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                      stroke="url(#arrowGradient)"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="text-muted-foreground">Loading chat history...</span>
                </div>
              </div>
            ) : sessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <svg className="w-16 h-16 text-muted-foreground/50 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h3 className="text-lg font-semibold text-foreground mb-2">No chat history yet</h3>
                <p className="text-muted-foreground mb-6">Start a new conversation to see it here</p>
                <button
                  onClick={() => navigate('/codemate')}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all shadow-lg font-medium"
                >
                  Start New Chat
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {sessions.map((session) => (
                    <button
                      key={session.session_id}
                      onClick={() => handleSessionClick(session.session_id)}
                      className="w-full text-left bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-5 hover:bg-card hover:border-border transition-all shadow-md hover:shadow-lg group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <span className="text-sm font-medium text-muted-foreground">
                              {formatDate(session.updated_at)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              • {session.message_count} message{session.message_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <p className="text-foreground font-medium group-hover:text-primary transition-colors line-clamp-2">
                            {session.preview || 'Chat conversation'}
                          </p>
                        </div>
                        <svg className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-lg transition-all shadow-lg font-medium disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {loadingMore ? (
                        <>
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Loading...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          <span>Load More</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatHistoryPage;
